//* 면세계산서/수기환불 요청 목록 공용 컨텐츠 — 페이지는 kind만 다른 두 개로 분리 운영.
//* seller: 자기 shop 접수 내역 확인 + 수정·재요청 / admin: 전체 대상 + 행 내 처리(완료·반려).
//* 신규 API 없음: 기존 find에 필터(status $in, 인덱스)·populate만 얹는다. seller find는
//* owningFilter가 서버에서 자동 스코프. 구서버에선 조회 실패/빈 목록 안내로 처리.
//* 계좌환불 행: 토스 가상계좌 건은 실제 이체 대신 **취소(자동환불)**이 주 동작(요청 계좌로 PG 환불,
//* 서버가 요청을 Done·refundedVia=PgAuto로 닫음). 실제 이체가 필요한 건 계좌입금·부트페이 가상계좌뿐.
//* (스펙: docs/shop-order-seller-request-spec.md · docs/shop-order-vbank-cancel-spec.md)

import { FC, useCallback, useEffect, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { OrderManualRefundRequestModal } from '@service/seller/components/OrderManualRefundRequestModal';
import { OrderTaxInvoiceRequestModal } from '@service/seller/components/OrderTaxInvoiceRequestModal';
import { OrderVbankCancelModal } from '@service/seller/components/OrderVbankCancelModal';
import {
  processShopOrderSellerRequest,
  revertShopOrderSellerRequest,
  ShopOrderSellerRequestKind,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderSellerRequestListUrl } from '@shared/api/shopOrder/shopOrder.hook';
import {
  ShopOrderCashReceiptStatus,
  ShopOrderManualRefund,
  ShopOrderPaymentProvider,
  ShopOrderRequestUser,
  ShopOrderSellerRequestRow,
  ShopOrderTaxInvoiceRequest,
} from '@shared/api/shopOrder/shopOrder.interface';
import {
  hasBothCashReceipts,
  hasLiveSelfCashReceipt,
  isTossVbankPayment,
  isVbankAutoRefundableRequest,
  selfCashReceiptAmount,
  sellerRequestStatusLabel,
  ShopOrderCashReceiptKind,
  shopOrderCashReceiptKind,
} from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole, PaymentState } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { Image } from 'antd';

type Kind = ShopOrderSellerRequestKind;
type StatusTab = 'pending' | 'processed';

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const processErrorMessage = (e: unknown): string => {
  switch (statusOf(e)) {
    case 400:
      return '처리할 수 없는 요청 상태입니다.';
    case 403:
      return '관리자만 처리할 수 있습니다.';
    case 404:
      return '주문을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.';
    case 409:
      return '요청 상태가 방금 바뀌었습니다(이미 처리됐을 수 있음). 새로고침 후 확인해주세요.';
    default:
      return '처리에 실패했습니다.';
  }
};

const dateString = (date?: Date) =>
  formatDate(date, { dateSeparater: '.', contains: { hours: true, minutes: true } });

//* 주문 식별용 짧은 날짜 (연도·시각 생략) — 결제일 등
const shortDate = (date?: Date) =>
  formatDate(date, { dateSeparater: '.', contains: { year: false } });

const userNameOf = (user?: ShopOrderRequestUser): string | undefined =>
  typeof user === 'object' ? user?.info?.name : undefined;

const requestOf = (
  row: ShopOrderSellerRequestRow,
  kind: Kind,
): ShopOrderTaxInvoiceRequest | ShopOrderManualRefund | undefined =>
  kind === 'taxInvoiceRequest' ? row.taxInvoiceRequest : row.manualRefund;

//* 모노톤 + 단일 상태 액센트(듀얼톤): 카드 좌측 바 + 상태 점만 색을 쓰고 나머지는 회색조.
const STATUS_ACCENT: Record<
  'Requested' | 'Done' | 'Rejected',
  { bar: string; dot: string }
> = {
  Requested: { bar: 'border-l-amber-400', dot: 'bg-amber-400' },
  Done: { bar: 'border-l-emerald-500', dot: 'bg-emerald-500' },
  Rejected: { bar: 'border-l-rose-400', dot: 'bg-rose-400' },
};

/* ── 면세계산서 처리 전 현금영수증 확인 ──────────────────────────────────
 * 실제 흐름: 마리가 전화로 요청을 받아 접수 → **우리 운영자가 현금영수증이 어디서
 * 발행됐는지 확인**(PG 자동발행인지 우리 자진발행인지) → 그 자리에서 취소(토스/부트페이
 * 콘솔 또는 홈택스) → 면세계산서 발행 → 완료 처리.
 *
 * 그래서 이 줄은 "체크박스"가 아니라 **어디서 무엇을 취소해야 하는지 알려주는 자리**다:
 * 발급처 · 금액 · 취소처 · 영수증 링크. 한 주문에 계산서와 현금영수증은 공존할 수 없다.
 *  - 우리 도장이 살아 있으면 미취소가 확정 → 체크 전까지 발행 완료 차단
 *  - PG 발행분·판정 불가는 시스템에 기록이 없어 확인(미체크 완료 시 confirm)까지만
 */
interface CashReceiptGuide {
  issuer: string; // 발급처
  detail: string; // 상태·유형 키워드
  amount?: number; // 발행액 (아는 경우)
  cancelAt: string; // 취소하러 갈 곳
  link?: string; // 영수증 링크
  className: string;
  note: string;
  //* 취소해야 할 현금영수증이 있는가 (false = 카드 매출전표)
  mustCancel: boolean;
  //* 우리 도장으로 미취소가 확인되는가 → 체크 없이 완료 처리 금지
  blocking: boolean;
}

const SELF_STAMP_LABEL: Record<ShopOrderCashReceiptStatus, string> = {
  [ShopOrderCashReceiptStatus.Issued]: '발행됨',
  [ShopOrderCashReceiptStatus.CancelRequested]: '취소 대기',
  [ShopOrderCashReceiptStatus.Canceled]: '취소 완료',
};

//* PG 이름 — 취소하러 갈 콘솔이 갈린다 (레거시 주문은 부트페이)
const pgLabel = (row: ShopOrderSellerRequestRow): string =>
  row.paymentDetail.provider === ShopOrderPaymentProvider.Tosspayments
    ? '토스'
    : '부트페이';

const cashReceiptGuide = (row: ShopOrderSellerRequestRow): CashReceiptGuide => {
  const stamp = row.selfCashReceiptDetail?.status;
  const alive = hasLiveSelfCashReceipt(row);
  const pg = pgLabel(row);
  const pgReceipt = row.paymentDetail.pgCashReceipt;
  if (stamp) {
    return {
      issuer: '우리 발행',
      detail: SELF_STAMP_LABEL[stamp] ?? stamp,
      amount: selfCashReceiptAmount(row),
      cancelAt: '홈택스',
      className: alive ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700',
      note: alive
        ? '홈택스 취소 후 체크 · 체크 시 자진발행 도장도 취소 · 미체크면 발행 완료 불가'
        : '자진발행분 취소 완료됨',
      mustCancel: alive,
      blocking: alive,
    };
  }
  switch (shopOrderCashReceiptKind(row)) {
    case ShopOrderCashReceiptKind.SelfIssued:
      //* 계좌입금인데 도장이 없다 = 우리가 아직 발행 전. 발행하지 말고 취소 도장으로 큐에서 뺀다
      return {
        issuer: '우리 발행',
        detail: '발행 기록 없음(계좌입금)',
        amount: selfCashReceiptAmount(row),
        cancelAt: '홈택스',
        className: 'bg-amber-100 text-amber-800',
        note: '홈택스에서 실제 발행분 확인 · 미발행이면 발행하지 말 것(체크하면 취소 기록으로 큐에서 제외)',
        mustCancel: true,
        blocking: false,
      };
    case ShopOrderCashReceiptKind.PgIssued: {
      //* 머니·포인트로 낸 간편결제는 발행 주체가 PG가 아니라 **간편결제사**다 —
      //* 토스 응답에 현금영수증이 안 실리는 이유이자, 취소하러 갈 곳이 다른 이유
      //* (docs/shop-order-easypay-cash-receipt-spec.md)
      const easyPay = row.paymentDetail.pgEasyPay?.provider;
      return {
        issuer: easyPay ? `${easyPay} 발행` : `${pg} 발행`,
        detail: pgReceipt?.type ?? row.paymentDetail.method,
        amount: pgReceipt?.amount ?? row.paymentDetail.paymentByCash,
        cancelAt: easyPay ? `${easyPay}/${pg} 콘솔` : `${pg} 콘솔`,
        link: pgReceipt?.receiptUrl ?? row.paymentDetail.receiptUrl,
        className: 'bg-amber-100 text-amber-800',
        note: '현금영수증 취소 확인 후 체크 · 우리 시스템엔 기록 없음(담당자 확인)',
        mustCancel: true,
        blocking: false,
      };
    }
    case ShopOrderCashReceiptKind.CardSlip:
      return {
        issuer: '없음',
        detail: '카드 매출전표',
        cancelAt: '-',
        link: row.paymentDetail.receiptUrl,
        className: 'bg-gray-100 text-gray-600',
        note: '취소할 현금영수증 없음 · 체크 금지',
        mustCancel: false,
        blocking: false,
      };
    default:
      return {
        issuer: '확인 필요',
        detail: row.paymentDetail.pgEasyPay?.provider
          ? `간편결제 (${row.paymentDetail.pgEasyPay.provider})`
          : '간편결제',
        amount: row.paymentDetail.paymentByCash,
        cancelAt: `${pg} 콘솔`,
        link: row.paymentDetail.receiptUrl,
        className: 'bg-amber-100 text-amber-800',
        note: '영수증 열어 확인(현금영수증/매출전표) · 현금영수증이면 취소 후 체크',
        mustCancel: true,
        blocking: false,
      };
  }
};

//* ─────────────────────────── 행 카드 ───────────────────────────
interface RowProps {
  row: ShopOrderSellerRequestRow;
  kind: Kind;
  isAdminUser: boolean;
  onChanged: () => void;
}

const RequestRowCard: FC<RowProps> = function RequestRowCard({
  row,
  kind,
  isAdminUser,
  onChanged,
}: RowProps) {
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const request = requestOf(row, kind);

  const modalOpenId = `orderRequestList-${kind}-${row._id}`;
  const setModalOpen = useSetRecoilState(simpleOpenAtomFamily(modalOpenId));
  const vbankCancelOpenId = `orderRequestList-vbank-${row._id}`;
  const setVbankCancelOpen = useSetRecoilState(simpleOpenAtomFamily(vbankCancelOpenId));

  //* admin 행 내 처리 입력
  const [resultNote, setResultNote] = useState('');
  //*! 기본 false — "확인"이 목적이라 기본 체크는 도장만 자동으로 찍히게 만든다
  const [cashReceiptCanceled, setCashReceiptCanceled] = useState(false);
  const [processing, setProcessing] = useState<'Done' | 'Rejected' | null>(null);
  const [reverting, setReverting] = useState(false);

  if (!request) return null;
  const taxRequest = kind === 'taxInvoiceRequest' ? row.taxInvoiceRequest : undefined;
  const refundRequest = kind === 'manualRefund' ? row.manualRefund : undefined;
  //* 계좌환불 행의 결제 갈래 — 토스 가상계좌는 실제 이체 없이 PG 자동환불이 되고,
  //* 그 외(계좌입금·부트페이 가상계좌)만 운영자가 실제로 이체한다.
  const tossVbank = !!refundRequest && isTossVbankPayment(row.paymentDetail);
  //* 대기(고객계좌환불) + 토스 가상계좌 = 요청 계좌로 자동환불(주 동작). 서버가 요청을 Done으로 닫는다.
  const autoRefundableRequest = !!refundRequest && isVbankAutoRefundableRequest(row);
  //* 반려된 토스 가상계좌 결제완료 건도 자동환불로 갈 수 있다(요청 계좌 프리필은 없음)
  const vbankCancelable =
    autoRefundableRequest ||
    (tossVbank &&
      refundRequest?.status === 'Rejected' &&
      row.paymentDetail.status === PaymentState.PAID);
  const autoRefunded = refundRequest?.refundedVia === 'PgAuto';
  const bizRegImage =
    typeof taxRequest?.bizRegImage === 'object' ? taxRequest.bizRegImage : undefined;
  const shopName = typeof row.shop === 'object' ? row.shop?.name : undefined;
  //* 면세계산서 처리 전 현금영수증 확인 (계산서 행에서만)
  const receiptGuide = taxRequest ? cashReceiptGuide(row) : undefined;
  const blockDone = !!receiptGuide?.blocking && !cashReceiptCanceled;

  //* 계산서/환불을 확인해서 처리하려면 어떤 주문인지 알아야 한다 — 주문 요약 필드들
  const item = row.orderDetail?.shopItemEmbed;
  const qty = row.orderDetail?.quantity ?? 1;
  const delivery = row.deliveryDetail;
  const bugoDetail = row.bugoDetail;

  const onProcess = async (status: 'Done' | 'Rejected') => {
    if (status === 'Rejected' && !resultNote.trim()) {
      notificateError(notifiacationInstance, '반려 사유를 처리 메모에 입력해주세요.');
      return;
    }
    //*! 계산서 발행 = 현금영수증이 없어야 한다. 취소를 실제로 했는지 여기서 확인시킨다
    if (kind === 'taxInvoiceRequest' && status === 'Done' && receiptGuide) {
      if (blockDone) {
        notificateError(
          notifiacationInstance,
          '자진발행 현금영수증 미취소 — 홈택스 취소 후 체크',
        );
        return;
      }
      if (
        receiptGuide.mustCancel &&
        !cashReceiptCanceled &&
        !window.confirm(
          `현금영수증 미취소(${receiptGuide.issuer} · ${receiptGuide.detail}) 상태로 발행 완료 기록.\n취소는 ${receiptGuide.cancelAt}에서 · 계산서+현금영수증 공존 = 매출 이중 계상. 계속할까요?`,
        )
      ) {
        return;
      }
      if (
        !receiptGuide.mustCancel &&
        cashReceiptCanceled &&
        !window.confirm(
          '카드 매출전표 — 취소할 현금영수증 없음.\n그래도 취소 기록을 남길까요?',
        )
      ) {
        return;
      }
    }
    setProcessing(status);
    try {
      await processShopOrderSellerRequest(row._id, kind, {
        status,
        resultNote: resultNote.trim() || undefined,
        //* 발행 완료(Done)일 때만 현금영수증 취소 플래그 전송. 반려에도 딸려가면
        //* cashReceiptCanceledAt이 찍혀 세무 기록에 허위 "취소" 도장이 남는다.
        ...(kind === 'taxInvoiceRequest' && status === 'Done'
          ? { cashReceiptCanceled }
          : {}),
      });
      notificateSuccess(
        notifiacationInstance,
        status === 'Done' ? '완료 처리했습니다.' : '반려 처리했습니다.',
      );
      onChanged();
    } catch (err) {
      console.error(err);
      notificateError(notifiacationInstance, processErrorMessage(err));
    } finally {
      setProcessing(null);
    }
  };

  //* 실수 처리 되돌리기 — 다시 대기(Requested)로. 서버가 부수효과를 정확히 역전한다:
  //* 계좌환불 Done이 취소확정까지 했던 건(cancelFinalized)이면 주문 취소 복귀 + 포인트 회수.
  //* ⚠️ 이미 나간 취소알림은 회수 불가 — 재처리 시 알림이 한 번 더 나가므로 미리 경고한다.
  const onRevert = async () => {
    const finalizedCancel =
      kind === 'manualRefund' &&
      request.status === 'Done' &&
      row.manualRefund?.cancelFinalized;
    const warn = finalizedCancel
      ? '이 건은 취소가 확정된 상태입니다. 되돌리면 주문이 취소요청으로 복귀하고 환불했던 포인트가 회수됩니다.\n\n※ 이미 고객에게 나간 취소알림은 회수되지 않으며, 다시 완료 처리하면 취소알림이 한 번 더 발송됩니다.\n\n대기 상태로 되돌릴까요?'
      : '이 처리를 취소하고 다시 대기 상태로 되돌릴까요?';
    if (!window.confirm(warn)) return;
    setReverting(true);
    try {
      await revertShopOrderSellerRequest(row._id, kind);
      notificateSuccess(notifiacationInstance, '대기 상태로 되돌렸습니다.');
      onChanged();
    } catch (err) {
      console.error(err);
      notificateError(
        notifiacationInstance,
        statusOf(err) === 400
          ? '되돌릴 수 없는 상태입니다. (이미 대기 상태이거나 아직 서버에 반영되지 않은 기능)'
          : processErrorMessage(err),
      );
    } finally {
      setReverting(false);
    }
  };

  //* 텍스트 옆에 붙는 소형 복사 버튼 — 홈택스/뱅킹앱에 옮겨 적는 값들에만
  const copyChip = (value: string) => (
    <button
      type="button"
      className="shrink-0 rounded border border-gray-200 px-1 text-[11px] leading-4 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      onClick={() => {
        navigator.clipboard.writeText(value);
        notificateSuccess(notifiacationInstance, '복사 완료');
      }}
    >
      복사
    </button>
  );

  const accent = STATUS_ACCENT[request.status];

  return (
    <div
      className={`space-y-2 rounded-lg border border-l-4 border-gray-200 bg-white p-3 text-sm ${accent.bar}`}
    >
      {/* 상단: 상태·상점 + 요청 메타 + 수정·재요청 (한 줄) */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${accent.dot}`} />
          <span className="font-medium text-gray-800">
            {sellerRequestStatusLabel[request.status]}
          </span>
        </span>
        {refundRequest && (
          <span className="text-gray-500">
            {refundRequest.target === 'SellerAdvance' ? '선지급 상환' : '고객계좌환불'}
          </span>
        )}
        {refundRequest && (
          <span
            className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
              tossVbank ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600'
            }`}
            title={
              tossVbank
                ? '토스 가상계좌 — 환불계좌만 있으면 PG가 자동환불(실제 이체 불필요)'
                : '계좌입금·부트페이 가상계좌 — 운영자가 실제 이체'
            }
          >
            {autoRefunded ? '자동환불 처리' : tossVbank ? '토스 가상계좌 · 자동환불' : '실제 이체'}
          </span>
        )}
        {shopName && <span className="text-gray-500">· {shopName}</span>}
        <span className="ml-auto flex items-center gap-2 text-xs text-gray-400">
          <span>
            요청 {dateString(request.requestedAt)}
            {userNameOf(request.requestedBy) && ` · ${userNameOf(request.requestedBy)}`}
          </span>
          {request.status !== 'Done' && (
            <button
              type="button"
              className="font-medium text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-gray-900"
              onClick={() => setModalOpen(true)}
            >
              수정·재요청
            </button>
          )}
        </span>
      </div>

      {/* 주문번호 전체 + 복사 (환불·계산서 대사 시 주문 식별 핵심) */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1">
        <span className="shrink-0 text-xs text-gray-400">주문번호</span>
        <span className="break-all font-mono text-xs text-gray-700">{row._id}</span>
        {copyChip(row._id)}
      </div>

      {/* 화환 상품명 — 헤딩처럼 강조 (어떤 상품 주문인지 한눈에) */}
      <div className="flex items-baseline justify-between gap-2">
        <p className="min-w-0 truncate font-medium text-gray-900">
          {item?.name ?? '-'}
          {qty > 1 && <span className="text-gray-500"> ×{qty}</span>}
        </p>
        <p className="shrink-0 font-semibold text-gray-900">
          {row.paymentDetail.paymentByCash.toLocaleString()}원
        </p>
      </div>

      {/* 주문 정보 — 라벨 정렬 표(줄글 대신 스캔 가능하게), 계산서/환불 확인용 */}
      <div className="grid grid-cols-[2.75rem_1fr] gap-x-2 gap-y-1 text-gray-700">
        <span className="text-gray-400">결제</span>
        <span>
          {row.paymentDetail.method}
          {row.paymentDetail.pgEasyPay?.provider
            ? ` (${row.paymentDetail.pgEasyPay.provider})`
            : ''}
          {row.paymentDetail.purchasedAt && (
            <span className="text-gray-500">
              {' '}
              · {shortDate(row.paymentDetail.purchasedAt)}
            </span>
          )}
          {item?.priceRetail != null &&
            item.priceRetail !== row.paymentDetail.paymentByCash && (
              <span className="text-gray-400">
                {' '}
                (판매가 {item.priceRetail.toLocaleString()}원)
              </span>
            )}
        </span>

        {(bugoDetail?.senderPhrase || bugoDetail?.condolencePhrase) && (
          <>
            <span className="text-gray-400">문구</span>
            <span className="min-w-0 break-keep">
              {bugoDetail?.senderPhrase && (
                <span className="block">보내는분 “{bugoDetail.senderPhrase}”</span>
              )}
              {bugoDetail?.condolencePhrase && (
                <span className="block text-gray-500">
                  조의 “{bugoDetail.condolencePhrase}”
                </span>
              )}
            </span>
          </>
        )}

        {delivery?.receiverAddress && (
          <>
            <span className="text-gray-400">배송</span>
            <span className="min-w-0 break-keep">
              <span className="block">{delivery.receiverAddress}</span>
              {(delivery.receiverName || delivery.receiverPhoneNumber) && (
                <span className="block text-gray-500">
                  {delivery.receiverName && `상주 ${delivery.receiverName}`}
                  {delivery.receiverName && delivery.receiverPhoneNumber && ' · '}
                  {delivery.receiverPhoneNumber &&
                    phoneNumberStringFormator(delivery.receiverPhoneNumber)}
                </span>
              )}
            </span>
          </>
        )}

        {(delivery?.senderName || delivery?.senderPhoneNumber) && (
          <>
            <span className="break-keep text-gray-400">주문자</span>
            <span className="flex flex-wrap items-center gap-1">
              <span>
                {delivery?.senderName ?? '-'}
                {delivery?.senderPhoneNumber &&
                  ` · ${phoneNumberStringFormator(delivery.senderPhoneNumber)}`}
              </span>
              {delivery?.senderPhoneNumber && copyChip(delivery.senderPhoneNumber)}
            </span>
          </>
        )}
      </div>

      {/* 요청 내용 — 썸네일은 텍스트 우측으로 붙여 세로 공간 절약 */}
      {taxRequest && (
        <div className="flex items-start gap-2 rounded-md bg-gray-50 p-2">
          <div className="min-w-0 flex-1 space-y-0.5">
            {taxRequest.bizNumber && (
              <p className="flex flex-wrap items-center gap-1">
                <span className="font-medium">사업자번호 {taxRequest.bizNumber}</span>
                {copyChip(taxRequest.bizNumber)}
              </p>
            )}
            {taxRequest.email && (
              <p className="flex flex-wrap items-center gap-1">
                <span className="break-all">이메일 {taxRequest.email}</span>
                {copyChip(taxRequest.email)}
              </p>
            )}
            {!taxRequest.bizNumber && !bizRegImage?.url && !taxRequest.email && (
              <p className="text-xs text-gray-500">첨부/입력된 사업자 정보 없음</p>
            )}
            {taxRequest.requestNote && (
              <p className="text-xs text-gray-600">메모: {taxRequest.requestNote}</p>
            )}
          </div>
          {bizRegImage?.url && (
            <Image
              src={fileBucketUrl(bizRegImage.url, 'w200')}
              alt="사업자등록증"
              preview={{ src: fileBucketUrl(bizRegImage.url, 'raw') }}
              width={72}
              className="max-h-24 shrink-0 rounded border object-contain"
            />
          )}
        </div>
      )}
      {refundRequest && (
        <div className="space-y-0.5 rounded-md bg-gray-50 p-2">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <span className="font-medium">
              {refundRequest.account.bank} {refundRequest.account.accountNumber} (
              {refundRequest.account.holderName})
            </span>
            {copyChip(
              `${refundRequest.account.bank} ${refundRequest.account.accountNumber} ${refundRequest.account.holderName}`,
            )}
            <span>
              ·{' '}
              <span className="font-bold">{refundRequest.amount.toLocaleString()}원</span>
            </span>
            {refundRequest.amount !== row.paymentDetail.paymentByCash && (
              <span className="text-xs text-gray-500">
                (결제 현금액 {row.paymentDetail.paymentByCash.toLocaleString()}원과 다름)
              </span>
            )}
          </p>
          {refundRequest.requestNote && (
            <p className="text-xs text-gray-600">메모: {refundRequest.requestNote}</p>
          )}
        </div>
      )}

      {/* 처리 결과 (완료/반려) */}
      {request.status !== 'Requested' && (
        <div className="text-xs text-gray-600">
          <p>
            {autoRefunded ? '자동환불(토스) 접수' : '처리'} {dateString(request.processedAt)}
            {userNameOf(request.processedBy) && ` · ${userNameOf(request.processedBy)}`}
          </p>
          {autoRefunded && (
            <p className="break-keep text-gray-500">
              요청 계좌로 토스 자동환불 — 실제 이체 없음. 입금까지 영업일 1~2일.
            </p>
          )}
          {taxRequest?.cashReceiptCanceledAt && (
            <p>현금영수증 취소 {dateString(taxRequest.cashReceiptCanceledAt)}</p>
          )}
          {request.resultNote && (
            <p className="text-gray-600">
              {request.status === 'Rejected' ? '반려 사유' : '처리 메모'}:{' '}
              {request.resultNote}
            </p>
          )}
          {/* 실수 처리 되돌리기 (admin) — 다시 대기로. 자동환불로 닫힌 건은 돈이 PG로 이미
              나갔으므로 되돌릴 수 없다(서버도 400) */}
          {isAdminUser && !autoRefunded && (
            <button
              type="button"
              className="mt-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              disabled={reverting}
              onClick={onRevert}
            >
              {reverting ? '되돌리는 중...' : '↩ 대기로 되돌리기'}
            </button>
          )}
        </div>
      )}

      {/* 토스 가상계좌 대기 건 — 실제 이체 대신 요청 계좌로 자동환불(주 동작). seller(마리)도 누를 수 있다
          (배송관리 카드의 같은 버튼과 동일 API). admin은 아래 처리 줄에도 같은 버튼이 있다 */}
      {autoRefundableRequest && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-sky-50 p-2">
          <p className="min-w-0 flex-1 break-keep text-xs text-sky-800">
            토스 가상계좌 — 실제 이체 없이 요청 계좌로 <b>자동환불</b>하면 이 요청은 완료로
            닫힙니다.
          </p>
          <Button
            className="border-none bg-warn px-3 text-xs text-white hover:bg-warn"
            onClick={() => setVbankCancelOpen(true)}
          >
            취소(자동환불)
          </Button>
        </div>
      )}

      {/* admin 행 내 처리 — 체크박스·메모·버튼을 한 줄에 (좁으면 자동 줄바꿈) */}
      {isAdminUser && request.status === 'Requested' && (
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 p-2">
          {/*! 계산서 발행 전 현금영수증 확인 — 어디서 발행됐고 어디서 취소하는지.
              우리 도장으로 미취소가 확인되면 체크 전까지 발행 완료를 막는다 */}
          {kind === 'taxInvoiceRequest' && receiptGuide && (
            <div className="w-full space-y-1 border-b border-gray-200 pb-1.5">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-xs font-medium text-gray-700">현금영수증</span>
                <span
                  className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${receiptGuide.className}`}
                >
                  {receiptGuide.issuer} · {receiptGuide.detail}
                </span>
                {receiptGuide.amount != null && receiptGuide.amount > 0 && (
                  <span className="text-xs text-gray-700">
                    {receiptGuide.amount.toLocaleString()}원
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  취소 {receiptGuide.cancelAt}
                </span>
                {receiptGuide.link && (
                  <a
                    href={receiptGuide.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-600 underline"
                  >
                    영수증
                  </a>
                )}
                {/* 마리에 확인 결과를 그대로 전달할 수 있게 한 줄 복사 */}
                {copyChip(
                  `현금영수증 ${receiptGuide.issuer} · ${receiptGuide.detail}${
                    receiptGuide.amount
                      ? ` · ${receiptGuide.amount.toLocaleString()}원`
                      : ''
                  } · 취소 ${receiptGuide.cancelAt}`,
                )}
                <label className="flex items-center gap-1 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={cashReceiptCanceled}
                    onChange={(e) => setCashReceiptCanceled(e.target.checked)}
                  />
                  취소 완료 확인
                </label>
              </div>
              <p
                className={`break-keep text-[11px] ${
                  blockDone ? 'font-medium text-red-600' : 'text-gray-500'
                }`}
              >
                {receiptGuide.note}
              </p>
            </div>
          )}
          {/*! 한 주문에 PG 발행분과 우리 자진발행분(상품교체 차액)이 함께 있는 경우.
              한쪽만 취소하고 계산서를 발행하면 매출이 두 번 잡힌다 */}
          {kind === 'taxInvoiceRequest' && hasBothCashReceipts(row) && (
            <p className="w-full break-keep text-xs text-red-600">
              현금영수증 2건 (PG 발행분 + 교체차액 자진발행분) — <b>둘 다 취소</b>
            </p>
          )}
          <input
            type="text"
            className="min-w-[160px] flex-1 rounded border border-gray-300 bg-white px-2 py-1"
            placeholder={
              kind === 'taxInvoiceRequest'
                ? '승인번호 / 반려 사유'
                : '이체 메모 / 반려 사유'
            }
            value={resultNote}
            onChange={(e) => setResultNote(e.target.value)}
          />
          <div className="ml-auto flex gap-1.5">
            <Button
              className="border border-gray-300 px-3 text-xs text-gray-600 hover:bg-gray-100"
              disabled={processing !== null}
              onClick={() => onProcess('Rejected')}
            >
              {processing === 'Rejected' ? '처리 중...' : '반려'}
            </Button>
            <Button
              className={
                //* 토스 가상계좌 건에서 "이체 완료"는 예외 경로(토스 거절 등) — 주 동작 아님을 색으로
                autoRefundableRequest
                  ? 'border border-gray-300 bg-white px-3 text-xs text-gray-600 hover:bg-gray-100'
                  : 'filled-gray-800 px-3 text-xs'
              }
              disabled={processing !== null || blockDone}
              title={
                blockDone
                  ? '현금영수증 취소 확인 후 발행 완료 가능'
                  : autoRefundableRequest
                  ? '토스 가상계좌 건은 실제 이체가 필요 없습니다 — 자동환불이 거절된 경우에만'
                  : undefined
              }
              onClick={() => onProcess('Done')}
            >
              {processing === 'Done'
                ? '처리 중...'
                : kind === 'taxInvoiceRequest'
                ? '발행 완료'
                : '이체 완료'}
            </Button>
          </div>
        </div>
      )}

      {kind === 'taxInvoiceRequest' ? (
        <OrderTaxInvoiceRequestModal
          openId={modalOpenId}
          order={row}
          onSuccess={onChanged}
        />
      ) : (
        <OrderManualRefundRequestModal
          openId={modalOpenId}
          order={row}
          onSuccess={onChanged}
          onSwitchToAutoRefund={
            vbankCancelable ? () => setVbankCancelOpen(true) : undefined
          }
        />
      )}
      {vbankCancelable && (
        <OrderVbankCancelModal openId={vbankCancelOpenId} order={row} onSuccess={onChanged} />
      )}
    </div>
  );
};

//* ─────────────────────────── 목록 컨텐츠 ───────────────────────────
interface Props {
  kind: Kind;
}

//* 처리 완료 목록 기간 필터 — 완료 건은 무한히 쌓이므로 기본 최근 1주만.
type ProcessedPeriod = '1week' | '1month' | '3month' | 'all';

const PROCESSED_PERIODS: { key: ProcessedPeriod; label: string; days?: number }[] = [
  { key: '1week', label: '최근 1주', days: 7 },
  { key: '1month', label: '최근 1개월', days: 30 },
  { key: '3month', label: '최근 3개월', days: 90 },
  { key: 'all', label: '전체' },
];

const periodFrom = (period: ProcessedPeriod): Date | undefined => {
  const days = PROCESSED_PERIODS.find((p) => p.key === period)?.days;
  if (!days) return undefined;
  const from = new Date();
  from.setDate(from.getDate() - days);
  return from;
};

export const OrderRequestListContent: FC<Props> = function OrderRequestListContent({
  kind,
}: Props) {
  const { sellerRole } = useAuth();
  const role = sellerRole();
  const isAdminUser = role === LowerRole.Admin;

  const [statusTab, setStatusTab] = useState<StatusTab>('pending');
  const [period, setPeriod] = useState<ProcessedPeriod>('1week');
  const [pendingRows, setPendingRows] = useState<ShopOrderSellerRequestRow[] | null>(
    null,
  );
  const [processedRows, setProcessedRows] = useState<ShopOrderSellerRequestRow[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  //* 대기(Requested)는 전량, 완료(Done|Rejected)는 기간 이후만 — 각각 별도 조회
  const loadPending = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api_.get<ShopOrderSellerRequestRow[]>(
        shopOrderSellerRequestListUrl(kind, role, 'pending'),
      );
      setPendingRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setPendingRows([]);
      setError(
        '요청 목록을 불러오지 못했습니다. (서버에 아직 반영되지 않은 기능일 수 있습니다)',
      );
    }
  }, [kind, role]);

  const loadProcessed = useCallback(async () => {
    try {
      const { data } = await api_.get<ShopOrderSellerRequestRow[]>(
        shopOrderSellerRequestListUrl(kind, role, 'processed', periodFrom(period)),
      );
      setProcessedRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setProcessedRows([]);
    }
  }, [kind, role, period]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);
  useEffect(() => {
    loadProcessed();
  }, [loadProcessed]);

  const reload = () => {
    loadPending();
    loadProcessed();
  };

  const visibleRows = statusTab === 'pending' ? pendingRows : processedRows;
  const pendingCount = pendingRows?.length ?? 0;
  const processedCount = processedRows?.length ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium leading-6 text-gray-900">
          {kind === 'taxInvoiceRequest' ? '면세계산서 요청' : '계좌환불 요청'}
        </h2>
        <Button className="border border-gray-300 text-sm" onClick={reload}>
          새로고침
        </Button>
      </div>
      <p className="break-keep text-sm text-gray-600">
        {kind === 'taxInvoiceRequest'
          ? '현금영수증 취소 → 면세계산서 발행'
          : '계좌입금 건은 실제 이체 · 토스 가상계좌 건은 취소(자동환불)'}
        {isAdminUser ? ' · 대기 건은 행에서 완료·반려' : ' · 반려 건은 수정 후 재요청'}
      </p>

      <div className="flex flex-wrap items-center gap-1.5">
        {(
          [
            ['pending', `처리 대기 (${pendingCount})`],
            ['processed', `처리 완료 (${processedCount})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`rounded-full border px-3 py-1 text-sm ${
              statusTab === key
                ? 'border-gray-800 bg-gray-800 font-medium text-white'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setStatusTab(key)}
          >
            {label}
          </button>
        ))}
        {/* 완료 탭에서만 기간 필터 — 완료 건은 무한히 쌓이므로 기본 최근 1주 */}
        {statusTab === 'processed' && (
          <div className="ml-2 flex flex-wrap items-center gap-1 border-l pl-2">
            {PROCESSED_PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`rounded-full border px-2.5 py-0.5 text-xs ${
                  period === p.key
                    ? 'border-gray-700 bg-gray-700 text-white'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <p className="break-keep text-sm text-red-600">{error}</p>}

      {visibleRows === null ? (
        <Loading />
      ) : visibleRows.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">
          {statusTab === 'pending'
            ? '처리 대기 중인 요청이 없습니다.'
            : `해당 기간(${
                PROCESSED_PERIODS.find((p) => p.key === period)?.label
              })에 처리된 요청이 없습니다.`}
        </p>
      ) : (
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
          {visibleRows.map((row) => (
            <RequestRowCard
              key={row._id}
              row={row}
              kind={kind}
              isAdminUser={isAdminUser}
              onChanged={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
};
